import moment from 'moment-timezone';
import find from 'lodash/find';
import get from 'lodash/get';
import reduce from 'lodash/reduce';
import uniq from 'lodash/uniq';
import sortBy from 'lodash/sortBy';
import pick from 'lodash/pick';
import cloneDeep from 'lodash/cloneDeep';
import range from 'lodash/range';
import filter from 'lodash/fp/filter';
import matchesProperty from 'lodash/fp/matchesProperty';
import pipe from 'ramda/src/pipe';
import reject from 'ramda/src/reject';
import map from 'ramda/src/map';
import slice from 'ramda/src/slice';
import {
  formatTime,
  prepareTimes,
  hoursToPeriod,
  addHours,
  getUTCStartOfDay,
  periodToTime,
} from './times';

import { showNotification } from './notifications';
import config from '../../../config';

export function isScheduleCurrent(schedule, currentDate) {
  return (
    moment(schedule.startDate).isSameOrBefore(currentDate, 'day') &&
    (!schedule.endDate || moment(schedule.endDate).isSameOrAfter(currentDate, 'day'))
  );
}

export function getCurrentWorkday(workdays, currentDate) {
  return workdays.find(day => day.weekDay === moment(currentDate).isoWeekday() && day.enabled);
}

export function getCurrentSchedule(allSchedules, currentDate) {
  const schedules = sortBy(allSchedules, schedule => moment(schedule.startDate).toDate());
  return find(schedules, schedule => isScheduleCurrent(schedule, currentDate));
}

const sliceTenItems = arr => slice(0, 10, arr || []);

// this function is for arrays days_off and days_on at the barber
export function getCurrentDayFromArray(currentDate, days) {
  return days.find(day => sliceTenItems(currentDate) === sliceTenItems(day.day));
}

export function filterTodayBlockTime(currentDate, days) {
  return days.filter(day => sliceTenItems(currentDate) === sliceTenItems(day.day));
}

const scheduleToWorkHours = (schedule) => {
  const period = hoursToPeriod(schedule);
  return [periodToTime(period.start, period.end)];
};

export function getWorkHours(barber, currentDay) {
  const emptySchedule = [{
    start: '08:00',
    end: '22:00',
  }];

  const schedules = barber && barber.schedules;
  if ((!schedules || !schedules.length) && !barber.days_on) {
    return {
      empty: true,
      workHours: emptySchedule,
    };
  }

  const currentSchedule = getCurrentSchedule(schedules, currentDay);
  let workHours = [];

  if (currentSchedule) {
    const todaySchedule = getCurrentWorkday(currentSchedule.workdays, currentDay);
    const dayOn = getCurrentDayFromArray(currentDay, barber.days_on);

    if (todaySchedule) {
      workHours = scheduleToWorkHours(todaySchedule);
    }

    const dayOff = getCurrentDayFromArray(currentDay, onlyWholeDays(barber.days_off));
    if (dayOff) {
      workHours = [];
    }

    if (dayOn) {
      workHours = addHours(dayOn);
    }
  }

  return {
    empty: !workHours.length,
    workHours,
  };
}

export function generateDays(enabled = false) {
  // TODO (0, 7) for Sunday in the first place in schedules, but it requires a lot of changes
  return range(1, 8).map(day => ({
    weekDay: day,
    ...prepareTimes(config.defaultWorkHours),
    enabled,
  }));
}

export function generateDaysWithShopTimes(enabled = false, shopSchedules) {
  if (!shopSchedules || !shopSchedules.length) {
    return generateDays(enabled);
  }

  let currentShopSchedule = shopSchedules[0];
  if (shopSchedules.length > 1 && moment(currentShopSchedule.startDate).isAfter(moment(shopSchedules[1].startDate))) {
    currentShopSchedule = shopSchedules[1];
  }

  return currentShopSchedule.workdays.map(day => ({
    weekDay: day.weekDay,
    startTime: formatTime(day.startHour, day.startMinute),
    endTime: formatTime(day.finishHour, day.finishMinute),
    enabled,
  }));
}

export function prepareSchedule(allSchedules) {
  if (!allSchedules || !allSchedules.length) {
    return {};
  }

  let schedules = sortBy(allSchedules, schedule => schedule.startDate);

  schedules = schedules.map((schedule) => {
    const workdays = schedule.workdays.map(day => ({
      weekDay: day.weekDay,
      startTime: formatTime(day.startHour, day.startMinute),
      endTime: formatTime(day.finishHour, day.finishMinute),
      enabled: day.enabled,
    }));

    return {
      id: schedule.id,
      startDate: moment(schedule.startDate, config.dateFormatISO).toDate(),
      endDate: schedule.endDate ? moment(schedule.endDate, config.dateFormatISO).toDate() : null,
      workdays,
    };
  });

  return {
    current: get(schedules, '[0]'),
    future: get(schedules, '[1]', { workdays: generateDays(true) }),
  };
}

export const onlyWholeDays = filter(matchesProperty('wholeDay', true));
export const onlyBlockTimes = filter(matchesProperty('wholeDay', false));

const isDayInvalid = (day) => {
  if (day.wholeDay) {
    return false;
  }

  const start = moment(day.startTime, config.timeFormat);
  const end = moment(day.endTime, config.timeFormat);
  return start.isSameOrAfter(end);
};

export function prepareDaysForForm(allDays) {
  if (!allDays || !allDays.length) {
    return [];
  }
  const days = sortBy(allDays, day => moment(day.day).toDate());

  const rejectInvalidDays = reject(isDayInvalid);
  const formatDays = map(day => ({
    id: day.id,
    day: day.day,
    startTime: formatTime(day.startHour, day.startMinute),
    endTime: formatTime(day.finishHour, day.finishMinute),
    wholeDay: day.wholeDay,
  }));

  // include only valid days so validation won't fail
  return pipe(formatDays, rejectInvalidDays)(days);
}

export function generateTimes(wholeDay = false, increment = 30, customConfig) {
  const hours = customConfig || (wholeDay ? config.wholeDay : config.availableHours);
  const times = [];
  const time = moment({
    hour: hours.start.hours,
    minute: hours.start.minutes,
  });
  const end = moment({
    hour: hours.end.hours,
    minute: hours.end.minutes,
  });
  while (time.isBefore(end)) {
    times.push(time.format(config.timeFormat));
    time.add(increment, 'minutes');
  }
  return times;
}

export function prepareScheduleForSaving(schedules) {
  const singleSchedule = (item) => {
    // Clone to avoid strange reference issue between two schedules
    const schedule = cloneDeep(item);
    schedule.workdays = (schedule.workdays || []).map((workday) => {
      const start = moment(workday.startTime, config.timeFormat);
      const end = moment(workday.endTime, config.timeFormat);
      delete workday.startTime;
      delete workday.endTime;

      const data = {
        startHour: start.hour(),
        startMinute: start.minute(),
        finishHour: end.hour(),
        finishMinute: end.minute(),
      };

      return Object.assign({}, workday, data);
    });

    schedule.startDate = getUTCStartOfDay({
      day: moment(schedule.startDate).format(),
    }).toJSON();
    if (schedule.endDate) {
      schedule.endDate = getUTCStartOfDay({
        day: moment(schedule.endDate).format(),
      }).toJSON();
    } else {
      schedule.endDate = null;
    }
    return schedule;
  };

  if (schedules.future && schedules.future.startDate && schedules.current.endDate) {
    return [singleSchedule(schedules.current), singleSchedule(schedules.future)];
  }
  return [singleSchedule(schedules.current)];
}

export function prepareDayForSaving(day) {
  const start = moment(day.startTime, config.timeFormat);
  const end = moment(day.endTime, config.timeFormat);
  const result = {
    id: day.id,
    day: day.day,
    isRecurring: day.isRecurring,
    recurrEveryNumDays: day.recurrEveryNumDays,
    updateAllRecurring: day.updateAllRecurring,
    wholeDay: day.wholeDay,
    description: day.description,
  };

  if (!day.wholeDay) {
    result.startHour = start.hour();
    result.startMinute = start.minute();
    result.finishHour = end.hour();
    result.finishMinute = end.minute();
  }

  return result;
}

export function prepareDaysForSaving(days) {
  if (!days || !days.length) {
    return [];
  }

  return days.map(day => prepareDayForSaving(day));
}

export function prepareMinsForSaving(mins) {
  if (!mins) {
    return mins;
  }
  let preparedMins = 0;
  if (mins.hours) {
    preparedMins += parseInt(mins.hours, 10) * 60;
  }
  if (mins.minutes) {
    preparedMins += parseInt(mins.minutes, 10);
  }
  return preparedMins;
}

export function checkDate(days = [], date) {
  return days.some(day => moment.utc(date, config.dateFormatISO).isSame(day.day, 'day'));
}

export function checkPresentDays(days, timezone) {
  if (!(days && days.length)) {
    return false;
  }
  return days.some(day => (
    moment.utc(day.day, config.dateFormatISO).isSameOrAfter(getUTCStartOfDay({ timezone }), 'day')
  ));
}

export function getSchedulesValidationErrors(schedules) {
  let current = false;
  let future = false;

  if (schedules.current.startDate && schedules.current.endDate) {
    const start = moment(schedules.current.startDate, config.dateFormat);
    const end = moment(schedules.current.endDate, config.dateFormat);
    if (start.isAfter(end)) {
      current = true;
      showNotification({
        message: 'Current Schedule: Ending date(s) must be set after starting date(s)!',
      });
    }
  }

  if (schedules.future && schedules.future.startDate && schedules.future.endDate) {
    const start = moment(schedules.future.startDate, config.dateFormat);
    const end = moment(schedules.future.endDate, config.dateFormat);
    if (start.isAfter(end)) {
      future = true;
      showNotification({
        message: 'Future Schedule: Ending date(s) must be set after starting date(s)!',
      });
    }
  }

  if (!current && schedules.current && schedules.current.workdays) {
    schedules.current.workdays.forEach((day) => {
      const start = moment(day.startTime, config.timeFormat);
      const end = moment(day.endTime, config.timeFormat);
      if (start.isSameOrAfter(end)) {
        current = true;
        showNotification({
          message: 'Current Schedule: End time(s) must be set after start time(s)!',
        });
      }
    });
  }

  if (!future && schedules.future && schedules.future.workdays) {
    schedules.future.workdays.forEach((day) => {
      const start = moment(day.startTime, config.timeFormat);
      const end = moment(day.endTime, config.timeFormat);
      if (start.isSameOrAfter(end)) {
        future = true;
        showNotification({
          message: 'Future Schedule: End time(s) must be set after start time(s)!',
        });
      }
    });
  }

  return {
    currentScheduleError: current,
    futureScheduleError: future,
  };
}

export function getDaysValidationErrors(days) {
  if (!days || !days.length) {
    return false;
  }

  return days.some(isDayInvalid);
}

export function isTimeBusy(busyTimesArray, startTime, duration) {
  return busyTimesArray.some((busyTime) => {
    const start = moment(busyTime.start).subtract(duration, 'minutes');
    return startTime.isBetween(start, busyTime.end, 'minute');
  });
}

export function isWorkTime(time, workHours, day, timezone) {
  let work = false;
  if (!workHours.empty) {
    work = workHours.workHours.some((hours) => {
      const period = hoursToPeriod(hours, day, timezone);
      return time.isSameOrAfter(period.start) && time.isBefore(period.end);
    });
  }
  return work;
}

export function extractDays(schedule = {}) {
  return pick(schedule, ['startDate', 'endDate']);
}

export function weekdaysDisabled(allSchedules) {
  const reduceDays = (acc, workday) => {
    if (get(workday, 'enabled') === false) return [...acc, get(workday, 'weekDay')];
    return acc;
  };
  const reduceSchedules = (acc, schedule) => {
    const disabledDays = reduce(get(schedule, 'workdays'), reduceDays, []);
    return uniq([...acc, ...disabledDays]);
  };
  return reduce(allSchedules, reduceSchedules, []);
}
